import { useState, type FormEvent, type ReactNode } from "react";
import { Footer } from "../shell/Footer";
import { ApiError, booksApi, CONSTITUTIONS, PURPOSES, settingsApi, type Constitution, type Purpose } from "../lib/api";

// The financial year in India runs April–March; default the books to begin on 1 April of the current year.
export function defaultBooksBegin(today = new Date()): string {
  const y = today.getMonth() < 3 ? today.getFullYear() - 1 : today.getFullYear();
  return `${y}-04-01`;
}

export function NewCompany({ first, onCreated, onCancel, extra }: { first: boolean; onCreated: (id: string) => void; onCancel?: () => void; extra?: ReactNode }) {
  const [name, setName] = useState("");
  const [legal, setLegal] = useState("");
  const [begin, setBegin] = useState(defaultBooksBegin());
  const [yourName, setYourName] = useState("");
  const [kind, setKind] = useState<Constitution>("company");
  const [standard, setStandard] = useState(true);
  const [purpose, setPurpose] = useState<Purpose | "">("");
  const [tried, setTried] = useState(false);
  const nonProfit = kind === "trust" || kind === "society";
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setTried(true);
    if (nonProfit && !purpose) { setErr(`Choose what the ${kind} does.`); return; }
    setBusy(true); setErr(null);
    try {
      const id = await booksApi.createCompany(name, begin, legal || undefined, yourName || undefined, kind, standard);
      // what it does: its own accounts (a college's fees, scholarships, students …)
      if (nonProfit && purpose) await settingsApi.saveCompany(id, { purpose });
      onCreated(id);
    }
    catch (x) { setErr(x instanceof ApiError ? x.message : String(x)); }
    finally { setBusy(false); }
  };

  return (
    <main className="auth">
      <form className="card auth-card" onSubmit={submit} onKeyDown={(e) => { if (e.key === "Escape" && onCancel) onCancel(); }}>
        <h1>{first ? "Create your first company" : "New company"}</h1>
        {first && <p className="muted">You'll be its owner and can add others later.</p>}
        <label>Company name<input autoFocus required value={name} onChange={(e) => setName(e.target.value)} /></label>
        <label>Legal name <span className="muted small">(if different)</span><input value={legal} onChange={(e) => setLegal(e.target.value)} /></label>
        <label>What kind of organisation<select value={kind} onChange={(e) => setKind(e.target.value as Constitution)}>
          {CONSTITUTIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></label>
        {nonProfit && <label>What does the {kind} do?<select value={purpose} className={tried && !purpose ? "invalid" : undefined} onChange={(e) => setPurpose(e.target.value as Purpose)}>
          <option value="">Choose…</option>{PURPOSES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></label>}
        {nonProfit && purpose === "education" && <p className="muted small">Adds a school's or college's accounts (tuition, admission, examination, hostel and transport fees, scholarships and concessions, teaching and non-teaching salaries) and keeps students, each with a unique ID.</p>}
        <p className="muted small">Names the owners' funds in the chart of accounts: share capital, partners' contribution or capital accounts, or owner's capital. A trust or society gets a corpus fund and an Income and Expenditure Account in place of the Profit and Loss.</p>
        <label>Books begin on<input type="date" required value={begin} onChange={(e) => setBegin(e.target.value)} /></label>
        <p className="muted small">The first financial year runs April to March, starting from the year this date falls in.</p>
        <label className="check"><input type="checkbox" checked={standard} onChange={(e) => setStandard(e.target.checked)} /> Start with the standard chart of accounts</label>
        <p className="muted small">Sales, Purchases, Cost of Goods Sold, Salaries, Rent, Advertising and the other accounts most businesses use. Untick if you'll bring the books in from Tally, Zoho or another system with their own accounts.</p>
        <label>Your name <span className="muted small">(shown in the edit log)</span><input value={yourName} onChange={(e) => setYourName(e.target.value)} /></label>
        <button className="primary" disabled={busy}>{busy ? "Creating…" : "Create company"}</button>
        {onCancel && <button type="button" className="link" onClick={onCancel}>Cancel <kbd>Esc</kbd></button>}
        {err && <p className="msg err" role="status">{err}</p>}
        {extra}
      </form>
      <Footer kind="public" />
    </main>
  );
}
